import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, ApiError } from "../api";
import { useAuth } from "../auth";
import { Button, Field } from "../ui";
import {
  PasswordInput,
  PasswordPolicyChecklist,
  PasswordStrengthMeter,
  passwordMeetsPolicy,
} from "../components/PasswordInput";

const OAUTH_ERROR_COPY: Record<string, string> = {
  invite_invalid:
    "That Google account doesn't match the email your invite was sent to. Sign in with the invited address, or finish registration with a password below.",
  invalid_state: "Sign-in expired or was tampered with. Please try again.",
  token_exchange: "Google didn't accept the sign-in. Please try again.",
  userinfo: "Couldn't read your Google profile. Please try again.",
  no_email: "Your Google account didn't share a verified email.",
  inactive: "This account is deactivated. Contact Property Management.",
  google_not_configured: "Google sign-in isn't enabled on this server.",
  internal: "Sign-in failed. Please try again.",
};

export function RegisterPage() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Email-link arrivals carry ?code=… and ?email=… so both fields
  // pre-fill. Trim whitespace so a code copy/pasted with stray
  // newlines doesn't break the Google CTA's enabled gate.
  const initialCode = (searchParams.get("code") ?? "").trim();
  const initialEmail = (searchParams.get("email") ?? "").trim();
  const cameFromInviteLink = !!initialCode;

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: initialEmail,
    password: "",
    confirmPassword: "",
    inviteCode: searchParams.get("code") ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  const passwordsMatch =
    form.confirmPassword.length > 0 && form.password === form.confirmPassword;
  const passwordOk = passwordMeetsPolicy(form.password);
  const canSubmit = passwordOk && passwordsMatch;

  // Probe whether the server has Google OAuth configured. Hide the
  // button when not — it would otherwise always error.
  useEffect(() => {
    void api
      .get<{ google: boolean }>("/auth/providers")
      .then((r) => setGoogleEnabled(Boolean(r.google)))
      .catch(() => setGoogleEnabled(false));
  }, []);

  // Surface OAuth-callback errors that arrive via ?oauth_error=...
  useEffect(() => {
    const oauthError = searchParams.get("oauth_error");
    if (oauthError) {
      setError(OAUTH_ERROR_COPY[oauthError] ?? "Google sign-up failed.");
    }
  }, [searchParams]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Belt-and-braces client validation. The server validates the
    // same policy and is the source of truth, but this saves a
    // round-trip and the user a second of frustration.
    if (!passwordOk) {
      setError("Pick a password that meets all the requirements below.");
      return;
    }
    if (!passwordsMatch) {
      setError("Passwords don't match.");
      return;
    }

    setBusy(true);
    try {
      const { confirmPassword: _ignored, ...payload } = form;
      await api.post("/auth/register", payload);
      await refresh();
      nav("/projects", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | string;
        const code =
          typeof body === "object" && body && "error" in body ? body.error : null;
        setError(
          code === "invite_invalid"
            ? "That invite code isn't valid for this email."
            : code === "email_in_use"
              ? "An account with this email already exists."
              : code === "password_weak"
                ? "Password doesn't meet the requirements below."
                : "Registration failed.",
        );
      } else setError("Registration failed.");
    } finally {
      setBusy(false);
    }
  };

  const update =
    (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [k]: e.target.value });

  const setField = (k: keyof typeof form) => (v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  // The Google button forwards the current invite code to the OAuth
  // start endpoint; the server stashes it in a short-lived HttpOnly
  // cookie and uses it on callback to bootstrap a brand-new account.
  // Disabled when no code is present so the user doesn't end up on
  // the no_account error path.
  const trimmedCode = form.inviteCode.trim();
  const googleHref = useMemo(
    () =>
      trimmedCode
        ? `/auth/google/start?invite_code=${encodeURIComponent(trimmedCode)}`
        : null,
    [trimmedCode],
  );

  // When the user arrives directly from an invite email (the URL
  // carries ?code=…), Google sign-up is the fastest path: one tap,
  // no password to invent. Promote it above the form. When the
  // user lands here directly with no code, the form leads and the
  // Google button shows below the divider as a secondary path.
  const promoteGoogle = cameFromInviteLink && googleEnabled;

  const googleBlock = googleEnabled && (
    <div>
      {googleHref ? (
        <a
          href={googleHref}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-ink-200 bg-white px-4 py-2.5 text-sm font-medium text-ink-700 no-underline shadow-soft transition-colors hover:bg-sand-50 hover:no-underline"
        >
          <GoogleGlyph />
          Continue with Google
        </a>
      ) : (
        <button
          type="button"
          disabled
          title="Enter your invite code first."
          className="flex w-full items-center justify-center gap-2 rounded-md border border-ink-200 bg-white px-4 py-2.5 text-sm font-medium text-ink-400 shadow-soft opacity-60 cursor-not-allowed"
        >
          <GoogleGlyph />
          Continue with Google
        </button>
      )}
      <p className="mt-2 text-center text-xs text-ink-500">
        {googleHref
          ? "Use the same Google email your invite was sent to."
          : "Paste your invite code below to enable Google sign-up."}
      </p>
    </div>
  );

  const dividerBlock = googleEnabled && (
    <div className="my-4 flex items-center gap-3 text-xs text-ink-400">
      <span className="h-px flex-1 bg-ink-100" />
      <span>{promoteGoogle ? "or use a password" : "or"}</span>
      <span className="h-px flex-1 bg-ink-100" />
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center -mt-6 -mx-4 sm:-mx-6 lg:-mx-8 py-10 px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-ink-800">Create your account</h1>
          <p className="mt-1.5 text-ink-500 font-light">
            {cameFromInviteLink
              ? "Welcome — finish setting up your account using your invite from Property Management."
              : "Use the invite code from Property Management to get started."}
          </p>
        </div>
        <div className="card">
          {/*
            Email-link arrivals see the Google CTA on top so the
            most likely action ("just sign in with Google, no
            new password to invent") is the first thing they
            see. Direct /register loads keep the form-first
            layout — there's no obvious one-click path until the
            user has typed an invite code anyway.
          */}
          {promoteGoogle && (
            <>
              {googleBlock}
              {dividerBlock}
            </>
          )}

          <form onSubmit={onSubmit} className="space-y-1">
            <div className="field-row">
              <Field label="First name">
                <input
                  className="input"
                  value={form.firstName}
                  onChange={update("firstName")}
                  required
                />
              </Field>
              <Field label="Last name">
                <input
                  className="input"
                  value={form.lastName}
                  onChange={update("lastName")}
                  required
                />
              </Field>
            </div>
            <Field label="Email">
              <input
                className="input"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
              />
            </Field>
            <Field label="Password">
              <PasswordInput
                value={form.password}
                onChange={setField("password")}
                minLength={12}
                autoComplete="new-password"
                required
              />
              <PasswordStrengthMeter value={form.password} />
              <PasswordPolicyChecklist value={form.password} />
            </Field>
            <Field
              label="Confirm password"
              error={
                form.confirmPassword.length > 0 && !passwordsMatch
                  ? "Passwords don't match."
                  : undefined
              }
            >
              <PasswordInput
                value={form.confirmPassword}
                onChange={setField("confirmPassword")}
                minLength={12}
                autoComplete="new-password"
                required
              />
            </Field>
            <Field label="Invite code">
              <input
                className="input"
                value={form.inviteCode}
                onChange={update("inviteCode")}
                required
              />
            </Field>
            {error && <div className="alert-error">{error}</div>}
            <Button
              variant="primary"
              type="submit"
              className="w-full mt-2"
              loading={busy}
              disabled={busy || !canSubmit}
            >
              {busy ? "Creating…" : "Create account"}
              {!busy && <ArrowRight size={16} />}
            </Button>
          </form>

          {/*
            For direct /register loads, the Google CTA sits below
            the form as a secondary path. The button reads
            disabled until the invite code is typed; the hint
            copy explains why.
          */}
          {!promoteGoogle && googleEnabled && (
            <>
              {dividerBlock}
              {googleBlock}
            </>
          )}
        </div>
        <p className="mt-4 text-center text-sm text-ink-500">
          Have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-brand-700 hover:text-brand-800"
          >
            Sign in
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4c-7.5 0-14 4.1-17.7 10.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.9 39.8 16.4 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6l6.2 5.2c-.4.4 6.8-5 6.8-14.8 0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
